import { useState } from "react";
import { Select, Button, Control } from "./components";
import { accountRequest, type AdministrationView } from "./account-api";
import type { AccountScope } from "@proof/accounts";
export function AccountBird({
  data,
  scope,
  run,
  refresh,
}: {
  data: AdministrationView;
  scope: AccountScope;
  run: (work: () => Promise<unknown>, success?: string) => Promise<void>;
  refresh: () => Promise<void>;
}) {
  const [editing, setEditing] = useState<string>(),
    [name, setName] = useState(""),
    [region, setRegion] = useState<"us" | "eu">("us"),
    [apiKey, setApiKey] = useState(""),
    [capabilities, setCapabilities] = useState<string[]>(["workspace.read"]),
    [operation, setOperation] = useState("workspace.read"),
    [body, setBody] = useState("{}"),
    [result, setResult] = useState<unknown>(),
    [confirm, setConfirm] = useState(false);
  const current = data.birdConnections.find((row) => row.id === editing),
    ops = data.birdOperations;
  const execute = () =>
    void run(async () => {
      setResult(undefined);
      const payload = JSON.parse(body);
      if (!payload || typeof payload !== "object" || Array.isArray(payload))
        throw new Error("Enter a JSON object for the request.");
      setResult(
        await accountRequest("/bird/invoke", {
          id: current!.id,
          operation,
          body: payload,
        }),
      );
      await refresh();
    });
  return (
    <section>
      <h2>Bird integrations</h2>
      <p>
        Configure a private Bird connection for SMS, WhatsApp, Apple Messages,
        voice, verification, lookups, contacts, audiences, webhooks and
        realtime. For application email, use Email providers and select
        Bird.com.
      </p>
      <p>
        <a href="https://bird.com/docs/api" target="_blank" rel="noreferrer">
          Bird API reference and request fields
        </a>
        . Your Bird account must have the corresponding service enabled and API
        permissions.
      </p>
      <div className="button-row">
        {data.birdConnections.map((row) => (
          <Button
            key={row.id}
            onClick={() => {
              setEditing(row.id);
              setName(row.name);
              setRegion(row.region);
              setApiKey("");
              setCapabilities(row.capabilities);
              setOperation(row.capabilities[0]);
              setBody("{}");
              setResult(undefined);
              setConfirm(false);
            }}
          >
            {row.name}
          </Button>
        ))}
        <Button
          onClick={() => {
            setEditing(undefined);
            setName("");
            setApiKey("");
            setCapabilities(["workspace.read"]);
            setResult(undefined);
            setConfirm(false);
          }}
        >
          New connection
        </Button>
      </div>
      <form
        className="account-form"
        onSubmit={(event) => {
          event.preventDefault();
          void run(async () => {
            await accountRequest("/bird", {
              ...(current ? { id: current.id } : {}),
              name,
              scope: current?.scope || scope,
              region,
              ...(apiKey ? { apiKey } : {}),
              capabilities,
              expectedRevision: current?.revision || 0,
            });
            setApiKey("");
            await refresh();
          }, "Bird connection saved.");
        }}
      >
        <Control label="Connection name">
          <input
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Control>
        <Control label="Bird region">
          <Select
            value={region}
            onChange={(event) => setRegion(event.target.value as typeof region)}
          >
            <option value="us">US</option>
            <option value="eu">EU</option>
          </Select>
        </Control>
        <Control
          label={
            current ? "Replace API key (leave blank to keep)" : "Bird API key"
          }
        >
          <input
            type="password"
            autoComplete="new-password"
            required={!current}
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
          />
        </Control>
        <fieldset>
          <legend>Enabled operations</legend>
          {ops.map((op) => (
            <label className="bird-operation" key={op.id}>
              <input
                type="checkbox"
                checked={capabilities.includes(op.id)}
                onChange={(event) =>
                  setCapabilities((old) =>
                    event.target.checked
                      ? [...old, op.id]
                      : old.filter((id) => id !== op.id),
                  )
                }
              />
              {op.label}
            </label>
          ))}
        </fieldset>
        <Button type="submit" kind="primary" disabled={!capabilities.length}>
          Save Bird connection
        </Button>
      </form>
      {current && (
        <section>
          <h3>Run a Bird operation</h3>
          <p>
            Requests use the saved connection and return Bird’s response below.
            Sending operations contact real recipients.
          </p>
          <Control label="Operation">
            <Select
              value={operation}
              onChange={(event) => {
                setOperation(event.target.value);
                setBody(
                  event.target.value === "sms.send"
                    ? JSON.stringify(
                        {
                          to: "",
                          from: "",
                          text: "",
                          category: "transactional",
                        },
                        null,
                        2,
                      )
                    : "{}",
                );
                setResult(undefined);
                setConfirm(false);
              }}
            >
              {ops
                .filter((op) => current.capabilities.includes(op.id))
                .map((op) => (
                  <option key={op.id} value={op.id}>
                    {op.label}
                  </option>
                ))}
            </Select>
          </Control>
          {ops.find((op) => op.id === operation)?.method === "POST" && (
            <Control label="Request fields (JSON)">
              <textarea
                rows={10}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                spellCheck={false}
              />
            </Control>
          )}
          <Button
            onClick={() => {
              if (ops.find((op) => op.id === operation)?.method === "POST")
                setConfirm(true);
              else execute();
            }}
          >
            Run operation
          </Button>
          {confirm && (
            <div role="alert">
              <p>
                Send this request to Bird? This may send messages, start a call
                or create records.
              </p>
              <Button onClick={() => setConfirm(false)}>Cancel</Button>
              <Button
                kind="primary"
                onClick={() => {
                  setConfirm(false);
                  execute();
                }}
              >
                Yes, send request
              </Button>
            </div>
          )}
          {result !== undefined && (
            <pre className="bird-result" role="status">
              {JSON.stringify(result, null, 2)}
            </pre>
          )}
        </section>
      )}
    </section>
  );
}
